import React, { useContext } from "react";
import { 
    Card, 
    CardContent, 
    CardHeader, 
    Typography, 
    Box, 
    Button, 
    IconButton,
    TextField,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import YAML from "yaml";
import { WamlContext } from "../WAMLContent";
import PercentInput from "./PercentInput";
import { SLO_DEFAULTS, createDefaultCustomSlo } from "./constants";

const useStyles = makeStyles({
    card: {
        border: '1px solid #e0e0e0'
    },
    cardHeader: {
        backgroundColor: '#f5f5f5',
        '& .MuiCardHeader-title': {
            fontSize: '1.1rem',
            fontWeight: 'bold'
        }
    },
    customObjective: {
        marginBottom: '16px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px'
    },
    objectiveRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        flexWrap: 'wrap'
    },
    latencyRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px'
    },
    queryField: {
        width: '100%',
        marginBottom: '12px'
    },
    addButton: {
        marginTop: '8px'
    }
});

export interface SLOCustomCardProps {
    deployKey: string;
    wapp: any;
}

const SLOCustomCard = ({ deployKey, wapp }: SLOCustomCardProps) => {
    const classes = useStyles();
    const context = useContext(WamlContext);

    const customSlos = wapp?.deploy?.[deployKey]?.slos?.custom || [];
    const isEnabled = customSlos.length > 0;

    const updateWaml = (updatedCustomSlos: any[]) => {
        const newWapp = JSON.parse(JSON.stringify(wapp));
        
        if (!newWapp.deploy) newWapp.deploy = {};
        if (!newWapp.deploy[deployKey]) newWapp.deploy[deployKey] = {};
        if (!newWapp.deploy[deployKey].slos) newWapp.deploy[deployKey].slos = {};
        
        if (updatedCustomSlos.length > 0) {
            newWapp.deploy[deployKey].slos.custom = updatedCustomSlos;
        } else {
            delete newWapp.deploy[deployKey].slos.custom;
            // Clean up empty slos object
            if (Object.keys(newWapp.deploy[deployKey].slos).length === 0) {
                delete newWapp.deploy[deployKey].slos;
            }
        }

        context.modifyWaml(YAML.stringify(newWapp), { tab: "SLOs" });
    };

    const handleToggle = (enabled: boolean) => {
        if (enabled) {
            updateWaml([createDefaultCustomSlo(`${SLO_DEFAULTS.CUSTOM_SLO_NAME_PREFIX}-1`)]);
        } else {
            updateWaml([]);
        }
    };

    const addCustomSlo = () => {
        const newCustomSlos = [...customSlos, createDefaultCustomSlo(`${SLO_DEFAULTS.CUSTOM_SLO_NAME_PREFIX}-${customSlos.length + 1}`)];
        updateWaml(newCustomSlos);
    };

    const removeCustomSlo = (index: number) => {
        const newCustomSlos = [...customSlos];
        newCustomSlos.splice(index, 1);
        updateWaml(newCustomSlos);
    };

    const updateCustomSlo = (index: number, updatedSlo: any) => {
        const newCustomSlos = [...customSlos];
        newCustomSlos[index] = updatedSlo;
        updateWaml(newCustomSlos);
    };

    const updateCustomSloField = (index: number, path: string, value: string) => {
        const newSlo = JSON.parse(JSON.stringify(customSlos[index]));
        const keys = path.split('.');
        let current = newSlo;
        
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        
        updateCustomSlo(index, newSlo);
    };

    const addLatencyObjective = (sloIndex: number) => {
        const newSlo = JSON.parse(JSON.stringify(customSlos[sloIndex]));
        if (!newSlo.latency) newSlo.latency = [];
        newSlo.latency.push({
            percent: SLO_DEFAULTS.LATENCY_PERCENT,
            totalQuery: "",
            successQuery: ""
        });
        updateCustomSlo(sloIndex, newSlo);
    };

    const removeLatencyObjective = (sloIndex: number, latencyIndex: number) => {
        const newSlo = JSON.parse(JSON.stringify(customSlos[sloIndex]));
        if (newSlo.latency) {
            newSlo.latency.splice(latencyIndex, 1);
            if (newSlo.latency.length === 0) {
                delete newSlo.latency;
            }
            updateCustomSlo(sloIndex, newSlo);
        }
    };

    const toggleAvailability = (sloIndex: number, enabled: boolean) => {
        const newSlo = JSON.parse(JSON.stringify(customSlos[sloIndex]));
        if (enabled) {
            newSlo.availability = {
                percent: SLO_DEFAULTS.AVAILABILITY_PERCENT,
                totalQuery: "",
                successQuery: ""
            };
        } else {
            delete newSlo.availability;
        }
        updateCustomSlo(sloIndex, newSlo);
    };

    return (
        <Card className={classes.card}>
            <CardHeader
                className={classes.cardHeader}
                title="Custom SLOs"
                action={
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isEnabled}
                                onChange={(e) => handleToggle(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Enabled"
                    />
                }
            />
            {isEnabled && (
                <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Custom SLOs allow you to define objectives using your own Prometheus queries.
                        Use template variables: <code>{"{{ .App }}"}</code> for app name and <code>{"{{ .Cluster }}"}</code> for cluster ID.
                        <br />
                        <a href="https://switchboard.weavelab.ninja/catalog/default/component/devx-knowledgebase/docs/slos/#slos-v2" target="_blank" rel="noopener noreferrer">
                          click here for more info on custom slos
                        </a>
                    </Typography>
                    <Box mt={2} mb={2}>
                        <Typography variant="subtitle2" component="p" gutterBottom><strong>Examples:</strong></Typography>
                        <Typography variant="body2" component="p" gutterBottom>
                            • Total Query: <code>http_requests_total{`{app="{{ .App }}", cid="{{ .Cluster }}"}`}</code>
                        </Typography>
                        <Typography variant="body2" component="p" gutterBottom>
                            • Success Query: <code>http_requests_total{`{app="{{ .App }}", cid="{{ .Cluster }}", code!~"5.."}`}</code>
                        </Typography>
                    </Box>

                    {customSlos.map((customSlo: any, index: number) => (
                        <Accordion key={index} className={classes.customObjective}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">
                                    {customSlo.name || `Custom SLO ${index + 1}`}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeCustomSlo(index);
                                    }}
                                    color="secondary"
                                    style={{ marginLeft: 'auto', marginRight: '8px' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </AccordionSummary>
                            <AccordionDetails style={{ flexDirection: 'column' }}>
                                {/* Name Field */}
                                <TextField
                                    label="Name"
                                    value={customSlo.name || ""}
                                    onChange={(e) => updateCustomSloField(index, 'name', e.target.value)}
                                    className={classes.queryField}
                                    size="small"
                                />

                                {/* Availability Section */}
                                <Box mb={2}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!customSlo.availability}
                                                onChange={(e) => toggleAvailability(index, e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Availability Objective"
                                    />
                                    {customSlo.availability && (
                                        <Box mt={1}>
                                            <Box className={classes.objectiveRow}>
                                                <PercentInput
                                                    name={`custom.${index}.availability.percent`}
                                                    value={customSlo.availability.percent}
                                                    onChange={(e) => updateCustomSloField(index, 'availability.percent', e.target.value)}
                                                />
                                                <Typography>of requests will be successful</Typography>
                                            </Box>
                                            <TextField
                                                label="Total Query"
                                                placeholder="e.g., http_requests_total{app='{{ .App }}', cid='{{ .Cluster }}'}"
                                                value={customSlo.availability.totalQuery || ""}
                                                onChange={(e) => updateCustomSloField(index, 'availability.totalQuery', e.target.value)}
                                                className={classes.queryField}
                                                size="small"
                                                multiline
                                                rows={2}
                                            />
                                            <TextField
                                                label="Success Query"
                                                placeholder="e.g., http_requests_total{app='{{ .App }}', cid='{{ .Cluster }}', code!~'5..'}"
                                                value={customSlo.availability.successQuery || ""}
                                                onChange={(e) => updateCustomSloField(index, 'availability.successQuery', e.target.value)}
                                                className={classes.queryField}
                                                size="small"
                                                multiline
                                                rows={2}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Latency Section */}
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Latency Objectives
                                    </Typography>
                                    {customSlo.latency?.map((latency: any, latencyIndex: number) => (
                                        <Box key={latencyIndex} className={classes.latencyRow}>
                                            <Box style={{ width: '100%' }}>
                                                <Box className={classes.objectiveRow}>
                                                    <PercentInput
                                                        name={`custom.${index}.latency.${latencyIndex}.percent`}
                                                        value={latency.percent}
                                                        onChange={(e) => updateCustomSloField(index, `latency.${latencyIndex}.percent`, e.target.value)}
                                                    />
                                                    <Typography>of requests will meet latency target</Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLatencyObjective(index, latencyIndex)}
                                                        color="secondary"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                                <TextField
                                                    label="Total Query"
                                                    placeholder="e.g., http_request_duration_seconds_bucket{app='{{ .App }}', cid='{{ .Cluster }}', le='+Inf'}"
                                                    value={latency.totalQuery || ""}
                                                    onChange={(e) => updateCustomSloField(index, `latency.${latencyIndex}.totalQuery`, e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    style={{ marginBottom: '8px' }}
                                                />
                                                <TextField
                                                    label="Success Query"
                                                    placeholder="e.g., http_request_duration_seconds_bucket{app='{{ .App }}', cid='{{ .Cluster }}', le='0.5'}"
                                                    value={latency.successQuery || ""}
                                                    onChange={(e) => updateCustomSloField(index, `latency.${latencyIndex}.successQuery`, e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => addLatencyObjective(index)}
                                        className={classes.addButton}
                                        variant="outlined"
                                        size="small"
                                    >
                                        Add Latency Objective
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={addCustomSlo}
                        className={classes.addButton}
                        variant="contained"
                        color="primary"
                    >
                        Add Custom SLO
                    </Button>
                </CardContent>
            )}
        </Card>
    );
};

export default SLOCustomCard;
